import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { roundMoney } from "../utils/money";
import { updateDoc, arrayRemove, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import "./InvoicesPanel.scss";
import { CircularProgress, Divider, Pagination } from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";

const InvoicesPanel = ({ users, stackedOrders, orders }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [checkedOrders, setCheckedOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);

  const [balances, setBalances] = useState({});
  const usersPerPage = 10;

  // Build enriched stackedOrders using orders prop
  const enrichedStackedOrders = React.useMemo(() => {
    if (!Array.isArray(stackedOrders) || !Array.isArray(orders)) return [];

    return stackedOrders.map((stack) => {
      // Ensure we have an orders array inside the stack
      const orderIds = Array.isArray(stack.orders) ? stack.orders : [];
      // Find matching orders in the orders prop
      const matchingOrders = orderIds
        .map((id) => orders.find((ord) => ord.id === id))
        .filter(Boolean);

      // Calculate total cost from matching orders (rounded at the end by thousandth rule)
      const totalCostRaw = matchingOrders.reduce((sum, order) => {
        const val = Number(order?.total || 0);
        return sum + (Number.isFinite(val) ? val : 0);
      }, 0);
      const totalCost = roundMoney(totalCostRaw);

      return {
        ...stack,
        lastCleared: stack.lastCleared || null,
        matchingOrders,
        totalCost,
      };
    });
  }, [stackedOrders, orders]);

  async function fetchBalance(clientId) {
    try {
      const base = process.env.REACT_APP_GET_OUTSTANDING_BALANCE_URL;
      const url = `${base}?clientId=${clientId}`;
      const res = await fetch(url);
      const data = await res.json();
      return data.balance;
    } catch (error) {
      console.error("❌ Error getting client balance:", error);
      return 0;
    }
  }
  useEffect(() => {
    async function loadBalances() {
      const newBalances = {};
      for (const user of users) {
        if (user.freshbooksClientId?.id) {
          try {
            const balance = await fetchBalance(user.freshbooksClientId.id);
            newBalances[user.id] = balance;
          } catch (err) {
            console.error(`Error fetching balance for ${user.id}`, err);
            newBalances[user.id] = 0;
          }
        }
      }
      setBalances(newBalances);
    }

    loadBalances();
  }, [users]);

  const handleCreateInvoice = async () => {
    const selectedStack = enrichedStackedOrders.find(
      (o) => o.id === selectedOrder
    );
    if (!selectedStack) return;

    const checkedOrderObjs = selectedStack.matchingOrders.filter((ord) =>
      checkedOrders.includes(ord.id)
    );
    if (checkedOrderObjs.length === 0) {
      alert("Please select at least one order.");
      return;
    }

    const pictures = checkedOrderObjs.flatMap((ord) =>
      Array.isArray(ord?.pictures) ? ord.pictures : []
    );
    const matchingUser = users.find((u) => u.id === selectedStack.id);
    const customerId =
      matchingUser?.freshbooksClientId?.id ||
      matchingUser?.freshbooksClientId ||
      null;

    if (!customerId) {
      console.error("❌ No FreshBooks customer ID for:", matchingUser);
      alert(
        "This customer does not have a FreshBooks client ID. Cannot create invoice."
      );
      return;
    }

    setIsCreatingInvoice(true); // start loading
    try {
      const createInvoiceFn = httpsCallable(
        getFunctions(),
        "createConsolidatedInvoice"
      );
      const payload = {
        pictures,
        invoiceNumber: undefined,
        poNumber: "",
        emailRecipients: [], // optionally fill
        customerId,
        userId: matchingUser?.id,
        rate: matchingUser?.rate ?? 0,
        paymentType: "Net 30", // or dynamically set
        orderIds: checkedOrders,
      };

      const result = await createInvoiceFn(payload);

      // Destructure invoiceId and pdfUrl from result.data
      const { invoiceId, pdfUrl, paymentStatus } = result.data;
      console.log("✅ Invoice created:", { invoiceId, pdfUrl, paymentStatus });
      if (pdfUrl) {
        alert(`Invoice created! You can download it here:\n${pdfUrl}`);
        window.open(pdfUrl, "_blank");
      } else {
        alert(`Invoice created! ID: ${invoiceId}`);
      }

      // Optional: send email link to customer
      /*
      if (pdfUrl && matchingUser?.email) {
        const sendEmailFn = httpsCallable(
          getFunctions(),
          "sendConsolidatedInvoiceEmail"
        );
        await sendEmailFn({
          to: matchingUser.email,
          pdfUrl,
          invoiceId,
        });
        console.log(
          "📧 Consolidated invoice email sent to",
          matchingUser.email
        );
      }
      */
      try {
        const stackedOrderRef = doc(db, "stackedOrders", selectedStack.id);
        await updateDoc(stackedOrderRef, {
          orders: arrayRemove(...checkedOrders),
          lastCleared: serverTimestamp(),
        });
        console.log("✅ Removed selected orders & updated lastCleared.");
      } catch (updateError) {
        console.error("❌ Failed to update stackedOrders:", updateError);
      }

      try {
        const refreshFn = httpsCallable(
          getFunctions(),
          "refreshOrderPaymentStatus"
        );
        await refreshFn({ orderIds: checkedOrders });
        console.log("🔄 Payment statuses refreshed for:", checkedOrders);
      } catch (refreshErr) {
        console.warn(
          "⚠️ Failed to refresh payment status after invoice creation:",
          refreshErr
        );
      }
      setCheckedOrders([]);
      setSelectedOrder("");
    } catch (error) {
      console.error("❌ Error creating invoice:", error);
      alert("Failed to create invoice. See console for details.");
    } finally {
      setIsCreatingInvoice(false); // stop loading
    }
  };

  return (
    <div
      className="glass-text"
      style={{ fontFamily: "Anek Latin", color: "#5A6872" }}
    >
      {selectedOrder && (
        <>
          <div
            className="overlay-backdrop"
            onClick={() => {
              setSelectedOrder("");
              setCheckedOrders([]); // clears selected orders when closing overlay
            }}
          />
          <div className="order">
            <span className="name">Build Consolidated Invoice</span>
            <Divider />
            <div className="content">
              {(() => {
                const selectedStack = enrichedStackedOrders.find(
                  (o) => o.id === selectedOrder
                );
                if (
                  !selectedStack ||
                  selectedStack.matchingOrders.length === 0
                ) {
                  return (
                    <span
                      style={{
                        width: "100%",
                        margin: "0 auto",
                      }}
                    >
                      No available orders to combine.
                    </span>
                  );
                }
                return (
                  <div className="options">
                    <span>
                      Uninvoiced Orders: {selectedStack.matchingOrders.length}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {(() => {
                        const selectedStack = enrichedStackedOrders.find(
                          (o) => o.id === selectedOrder
                        );
                        const allOrderIds =
                          selectedStack?.matchingOrders?.map((ord) => ord.id) ||
                          [];
                        const allSelected =
                          allOrderIds.length > 0 &&
                          allOrderIds.every((id) => checkedOrders.includes(id));
                        return (
                          <button
                            id="selectAll"
                            onClick={() => {
                              if (!selectedStack) return;
                              if (allSelected) {
                                // unselect all
                                setCheckedOrders((prev) =>
                                  prev.filter((id) => !allOrderIds.includes(id))
                                );
                              } else {
                                // select all
                                setCheckedOrders((prev) =>
                                  Array.from(new Set([...prev, ...allOrderIds]))
                                );
                              }
                            }}
                          >
                            {allSelected
                              ? "Deselect All Orders"
                              : "Select All Orders"}
                          </button>
                        );
                      })()}
                    </div>

                    {selectedStack.matchingOrders.map((ord) => {
                      const isSelected = checkedOrders.includes(ord.id);
                      return (
                        <div
                          className={`option ${isSelected ? "selected" : ""}`}
                          key={ord.id}
                          onClick={() => {
                            setCheckedOrders((prev) => {
                              const newSelection = isSelected
                                ? prev.filter((id) => id !== ord.id)
                                : [...prev, ord.id];

                              console.log(
                                "📦 Currently selected orders:",
                                newSelection
                              );
                              return newSelection;
                            });
                          }}
                        >
                          <span id="orderId">Order ID: {ord.id}</span>
                          <div className="footer">
                            <span id="total">
                              Total: $
                              {roundMoney(Number(ord.total || 0)).toFixed(2)}
                            </span>
                            <span id="pictures">
                              Pictures:{" "}
                              {Array.isArray(ord.pictures)
                                ? ord.pictures.length
                                : 0}{" "}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <div className="options lineItems">
                {(() => {
                  const selectedStack = enrichedStackedOrders.find(
                    (o) => o.id === selectedOrder
                  );
                  if (!selectedStack) return null;
                  const checkedOrderObjs = selectedStack.matchingOrders.filter(
                    (ord) => checkedOrders.includes(ord.id)
                  );
                  if (checkedOrderObjs.length === 0) {
                    return <span>No orders selected.</span>;
                  }
                  return (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {checkedOrderObjs.map((ord) => (
                        <li
                          key={ord.id}
                          style={{
                            padding: "0.5rem 0",
                            borderBottom: "1px solid rgba(0,0,0,0.1)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "500",
                            }}
                          >
                            <span>Order ID:</span> {ord.id}
                          </div>
                          <div
                            style={{
                              fontWeight: "500",
                            }}
                          >
                            <span>Total:</span> $
                            {roundMoney(Number(ord.total || 0)).toFixed(2)}
                          </div>
                          {/* Pictures listing */}
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            {Array.isArray(ord.pictures) &&
                              ord.pictures.map((pic, idx) => {
                                const matchingUser = users.find(
                                  (u) => u.id === selectedStack.id
                                );
                                const rate =
                                  matchingUser?.rate ?? pic.rate ?? 0;
                                const totalLine = (() => {
                                  const widthHeight =
                                    Number(pic.widthIn) * Number(pic.heightIn);
                                  const rateTotal = roundMoney(
                                    widthHeight * Number(rate)
                                  );
                                  const quantityTotal = roundMoney(
                                    rateTotal * Number(pic.quantity)
                                  );
                                  return roundMoney(quantityTotal).toFixed(2);
                                })();
                                return (
                                  <div className="line" key={idx}>
                                    <span className="text">
                                      {pic.quantity} {pic.name} {pic.widthIn}x
                                      {pic.heightIn} @ $
                                      {roundMoney(rate).toFixed(2)} per square
                                      inch
                                    </span>
                                    <span className="total">${totalLine}</span>
                                  </div>
                                );
                              })}
                          </div>
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              </div>
            </div>
            <div className="controls">
              <button
                id="createInvoice"
                onClick={handleCreateInvoice}
                disabled={isCreatingInvoice}
              >
                {isCreatingInvoice ? (
                  <>
                    <CircularProgress
                      size={16}
                      style={{ marginRight: "6px" }}
                    />
                    Creating...
                  </>
                ) : (
                  "Create Invoice"
                )}
              </button>
            </div>
          </div>
        </>
      )}
      <div className="recent-orders">
        {[...enrichedStackedOrders]
          // only show stacks that still have uninvoiced items
          .filter((o) =>
            Array.isArray(o.matchingOrders)
              ? o.matchingOrders.length > 0
              : false
          )
          .sort((a, b) => {
            const aTime = a.lastCleared?.seconds || 0;
            const bTime = b.lastCleared?.seconds || 0;
            return aTime - bTime;
          })
          .slice(0, 4)
          .map((order) => (
            <div
              key={order.id}
              className="glass-panel recent"
              onClick={() => setSelectedOrder(order.id)}
            >
              <span id="date">
                Last Generated:{" "}
                {order.lastCleared?.toDate?.()?.toLocaleDateString() || "N/A"}
              </span>
              <span className="name">{order.customerName}</span>
              <div id="cardFooter">
                <span id="count">
                  Pending: {order.matchingOrders?.length || 0}
                </span>
                <span id="cost">${roundMoney(order.totalCost).toFixed(2)}</span>
              </div>
            </div>
          ))}
      </div>
      <div id="user-list" className="recent-orders list">
        <div className="searchHeader">
          <input
            className="search"
            label="Search"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <select
            className="search"
            value={sortOption}
            onChange={(e) => {
              setSortOption(e.target.value);
              setSortDirection("asc");
            }}
          >
            <option value="">Sort by...</option>
            <option value="firstName">Alphabetical (First Name)</option>
            <option value="lastName">Alphabetical (Last Name)</option>
            <option value="lastGenerated">Last Generated</option>
          </select>
          {sortOption && (
            <select
              className="search second-filter"
              value={sortDirection}
              onChange={(e) => setSortDirection(e.target.value)}
            >
              {sortOption === "lastGenerated" ? (
                <>
                  <option value="desc">Newest</option>
                  <option value="asc">Oldest</option>
                </>
              ) : (
                <>
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </>
              )}
            </select>
          )}
        </div>
        <Divider
          sx={{
            marginBottom: "0.5rem",
          }}
        />
        {(() => {
          // Filter users: only Net30 approved and by search term
          const filteredUsers = users
            .filter((user) => user.net30Approved) // only show Net30 approved users
            .filter((user) => {
              const fullName = `${(user.firstName || "").toLowerCase()} ${(user.lastName || "").toLowerCase()}`;
              const organization = (
                user.freshbooksClientId?.organization || ""
              ).toLowerCase();
              const term = searchTerm.toLowerCase();
              return fullName.includes(term) || organization.includes(term);
            });

          let sortedUsers = [...filteredUsers];

          if (sortOption === "firstName") {
            sortedUsers.sort((a, b) =>
              (a.firstName || "").localeCompare(b.firstName || "", undefined, {
                sensitivity: "base",
              })
            );
          } else if (sortOption === "lastName") {
            sortedUsers.sort((a, b) =>
              (a.lastName || "").localeCompare(b.lastName || "", undefined, {
                sensitivity: "base",
              })
            );
          } else if (sortOption === "lastGenerated") {
            sortedUsers.sort((a, b) => {
              const aDate =
                stackedOrders.find((o) => o.id === a.id)?.lastCleared
                  ?.seconds || 0;
              const bDate =
                stackedOrders.find((o) => o.id === b.id)?.lastCleared
                  ?.seconds || 0;
              return aDate - bDate;
            });
          }

          if (sortDirection === "desc") {
            sortedUsers.reverse();
          }

          const indexOfLastUser = currentPage * usersPerPage;
          const indexOfFirstUser = indexOfLastUser - usersPerPage;
          const currentUsers = sortedUsers.slice(
            indexOfFirstUser,
            indexOfLastUser
          );
          return (
            <>
              {currentUsers.map((user) => (
                <div
                  key={user.id}
                  className={`glass-panel user card ${balances[user.id] > 1000 ? " high-balance" : ""}`}
                  onClick={() => setSelectedOrder(user.id)}
                >
                  <span id="date">
                    Last Generated:{" "}
                    {(() => {
                      const match = stackedOrders.find((o) => o.id === user.id);
                      return (
                        match?.lastCleared?.toDate?.()?.toLocaleDateString() ||
                        "N/A"
                      );
                    })()}
                  </span>
                  <span
                    className="balance"
                    style={{
                      color: balances[user.id] > 1000 ? "#d03131ff" : "black",
                    }}
                  >
                    {user.freshbooksClientId?.id
                      ? `$${balances[user.id] ?? "Loading..."}`
                      : "No FreshBooks account"}
                  </span>
                  <span className="name">
                    {user.freshbooksClientId?.organization ||
                      `${user.firstName} ${user.lastName}`}
                  </span>
                  <span className="count">
                    Pending:{" "}
                    {(() => {
                      const match = stackedOrders.find((o) => o.id === user.id);
                      return match?.orders.length || "0";
                    })()}
                  </span>
                </div>
              ))}
              <div style={{ margin: "0 auto", marginBottom: "0.5rem" }}>
                <Pagination
                  count={Math.ceil(filteredUsers.length / usersPerPage)}
                  page={currentPage}
                  className="pagination"
                  onChange={(e, value) => setCurrentPage(value)}
                  color="primary"
                />
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default InvoicesPanel;
